import produce from "immer";
import { useContext, useEffect, useState } from "react";
import { useOrderFlow } from "../../../OrderFlowProvider/OrderFlowContext";
import { ModuleNames } from "../../../OrderFlowProvider/OrderFlowTypes";
import { RecipeFilterContext } from "./RecipeFilterContext";
import { buildFilterID, FilterTypesBaseKeys, handleChipDelete, handleFilterInputChange, InitialRecipeFilterVisibilityEnum, resetRecipeFilterState, stripNumAndSpacePattern, } from "./recipeFilterModuleUtils";
/**
 *
 * @param param0 Currently only the sequence number is required.
 * @returns Values req. to implement the filter module across the app.
 */
export const useRecipeFilterModule = ({ sequenceNumber }) => {
    var _a, _b;
    const { store } = useOrderFlow();
    // The filter-state that has been applied to the recipe selections.
    const { filterState, setFilterState } = useContext(RecipeFilterContext);
    // Recipe flags for all potential recipe selections, for use with flag filter inputs.
    const [allRecipeFlags, setAllRecipesFlags] = useState([]);
    const recipeFilterModuleConfig = (_a = store.model.Flow.FlowModules.find((module) => module.ModuleName === ModuleNames.RECIPE_FILTER &&
        module.Sequence === sequenceNumber &&
        module.Page === store.cart.currentPage)) === null || _a === void 0 ? void 0 : _a.ModuleConfig;
    const shouldExpandFilterSection = (_b = (recipeFilterModuleConfig === null || recipeFilterModuleConfig === void 0 ? void 0 : recipeFilterModuleConfig.InitialRecipeFilterVisibility) === InitialRecipeFilterVisibilityEnum.Expanded) !== null && _b !== void 0 ? _b : true;
    /**
     * This effect updates the available recipes for the flag filter module.
     * It only collects and sets the recipe flags to state if there are flag filters in the order flow.
     */
    useEffect(() => {
        let mounted = true;
        if (mounted &&
            recipeFilterModuleConfig.FilterSequenceBuilder.some((f) => f.filterType.replace(stripNumAndSpacePattern, "") === FilterTypesBaseKeys.RecipeFlag)) {
            setAllRecipesFlags((_) => store.model.Recipes.map((r) => r.RecipeFlags).flat());
        }
        return () => {
            mounted = false;
        };
    }, [store.model.Recipes]);
    // ===============
    // There's some object manipulation going on throughout the filter module; immer prevents any unwanted mutations.
    const immerFilterSequenceBuilder = produce(recipeFilterModuleConfig.FilterSequenceBuilder, (draft) => {
        draft.forEach((filter) => {
            filter.immerFilterType = buildFilterID(filter.filterType, filter.filterSequence);
            if (filter.filterType.replace(stripNumAndSpacePattern, "") === FilterTypesBaseKeys.RecipeFlag) {
                const flagFilterOptionsKeys = filter.filterOptions.map((o) => o.Value.toLocaleLowerCase());
                const specificFlags = allRecipeFlags.filter((f) => flagFilterOptionsKeys.some((t) => t.toLocaleLowerCase() === f.FlagName.toLocaleLowerCase()));
                // Removes duplicate specificFlags options
                const uniqueSpecificFlags = specificFlags.filter((item, index, self) => index === self.findIndex((obj) => obj.FlagName === item.FlagName && obj.FlagValue === item.FlagValue));
                filter.immerFilterOptions = uniqueSpecificFlags.map((f) => {
                    return {
                        // Order is important.
                        Label: f.FlagValue.toLocaleLowerCase().replace(/\b[a-z]/g, (letter) => letter.toUpperCase()),
                        Value: f.FlagValue,
                        immerFilterSequence: filter.filterSequence,
                        immerFilterType: filter.filterType,
                    };
                });
            }
            else {
                // Order is important.
                filter.immerFilterOptions = filter.filterOptions.map((f) => {
                    return {
                        Label: f.Label,
                        Value: f.Value,
                        immerFilterSequence: filter.filterSequence,
                        immerFilterType: filter.filterType,
                    };
                });
            }
        });
    });
    return {
        filterState,
        handleChange: handleFilterInputChange(setFilterState),
        handleChipDelete: handleChipDelete(filterState, setFilterState),
        immerFilterSequenceBuilder,
        resetRecipeFilterState: resetRecipeFilterState(setFilterState),
        shouldExpandFilterSection,
    };
};
